import { useState } from 'react'
import { Pencil, Trash2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useWasteTypes,
  useCreateWasteType,
  useUpdateWasteType,
  useDeleteWasteType,
} from '@/hooks/queries/useWasteTypes'
import { WasteTypesToolbar } from '@/pages/waste-types/components/WasteTypesToolbar'
import { MultiSelect } from '@/components/ui/multi-select'
import type { WasteCategory, WasteSubcategory, WasteType } from '@/types'

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; wasteType: WasteType }
  | { mode: 'delete'; wasteType: WasteType }

function getServerError(error: unknown): string | null {
  if (!error) return null
  const err = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }
  const errors = err.response?.data?.errors
  if (errors) return Object.values(errors).flat().join(' ')
  return err.response?.data?.message ?? 'Ocurrió un error inesperado'
}

interface Props {
  categories: WasteCategory[]
  subcategories: WasteSubcategory[]
}

export function WasteTypesTab({ categories, subcategories }: Props) {
  const { data: wasteTypes, isLoading } = useWasteTypes()
  const createMutation = useCreateWasteType()
  const updateMutation = useUpdateWasteType()
  const deleteMutation = useDeleteWasteType()

  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' })
  const [nameInput, setNameInput] = useState('')
  const [subcategoryId, setSubcategoryId] = useState<string>('')
  const [unitInput, setUnitInput] = useState('kg')
  const [emissionFactorInput, setEmissionFactorInput] = useState('')
  const [search, setSearch] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([])

  const openCreate = () => {
    setNameInput('')
    setSubcategoryId('')
    setUnitInput('kg')
    setEmissionFactorInput('')
    createMutation.reset()
    setDialog({ mode: 'create' })
  }

  const openEdit = (wasteType: WasteType) => {
    setNameInput(wasteType.name)
    setSubcategoryId(String(wasteType.waste_subcategory_id))
    setUnitInput(wasteType.unit)
    setEmissionFactorInput(
      wasteType.emission_factor !== null ? String(wasteType.emission_factor) : ''
    )
    updateMutation.reset()
    setDialog({ mode: 'edit', wasteType })
  }

  const openDelete = (wasteType: WasteType) => {
    deleteMutation.reset()
    setDialog({ mode: 'delete', wasteType })
  }

  const closeDialog = () => setDialog({ mode: 'closed' })

  const handleSubmit = async () => {
    if (!subcategoryId) return
    const emissionFactor = emissionFactorInput.trim() === ''
      ? null
      : Number(emissionFactorInput)

    if (dialog.mode === 'create') {
      await createMutation.mutateAsync({
        name: nameInput,
        waste_subcategory_id: Number(subcategoryId),
        unit: unitInput || 'kg',
        emission_factor: emissionFactor,
      })
      closeDialog()
    } else if (dialog.mode === 'edit') {
      await updateMutation.mutateAsync({
        id: dialog.wasteType.id,
        data: {
          name: nameInput,
          waste_subcategory_id: Number(subcategoryId),
          unit: unitInput || 'kg',
          emission_factor: emissionFactor,
        },
      })
      closeDialog()
    }
  }

  const handleDelete = async () => {
    if (dialog.mode !== 'delete') return
    await deleteMutation.mutateAsync(dialog.wasteType.id)
    closeDialog()
  }

  const isFormDialog = dialog.mode === 'create' || dialog.mode === 'edit'
  const activeMutation = dialog.mode === 'create' ? createMutation : updateMutation
  const formError = isFormDialog ? getServerError(activeMutation.error) : null
  const deleteError = dialog.mode === 'delete' ? getServerError(deleteMutation.error) : null

  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }))
  const subcategoryOptions = subcategories
    .filter((s) => selectedCategories.length === 0 || selectedCategories.includes(String(s.waste_category_id)))
    .map((s) => ({ value: String(s.id), label: s.name }))

  const handleCategoryChange = (values: string[]) => {
    setSelectedCategories(values)
    if (values.length > 0) {
      const validSubIds = subcategories
        .filter((s) => values.includes(String(s.waste_category_id)))
        .map((s) => String(s.id))
      setSelectedSubcategories((prev) => prev.filter((id) => validSubIds.includes(id)))
    }
  }

  const normalizedSearch = search.trim().toLowerCase()
  const filtered = (wasteTypes ?? []).filter((wt) => {
    if (selectedCategories.length > 0) {
      const catId = wt.subcategory?.waste_category_id
      if (!catId || !selectedCategories.includes(String(catId))) return false
    }
    if (selectedSubcategories.length > 0 && !selectedSubcategories.includes(String(wt.waste_subcategory_id))) return false
    if (normalizedSearch && !wt.name.toLowerCase().includes(normalizedSearch) && !wt.subcategory?.name.toLowerCase().includes(normalizedSearch) && !wt.subcategory?.category?.name.toLowerCase().includes(normalizedSearch)) return false
    return true
  })

  return (
    <>
      <CardHeader className="space-y-4">
        <div className="flex items-center justify-between">
          <WasteTypesToolbar
            search={search}
            onSearchChange={setSearch}
            onClearFilters={() => { setSearch(''); setSelectedCategories([]); setSelectedSubcategories([]) }}
            hasActiveFilters={Boolean(search.trim()) || selectedCategories.length > 0 || selectedSubcategories.length > 0}
          >
            <MultiSelect
              options={categoryOptions}
              selected={selectedCategories}
              onChange={handleCategoryChange}
              placeholder="Categorías"
            />
            <MultiSelect
              options={subcategoryOptions}
              selected={selectedSubcategories}
              onChange={setSelectedSubcategories}
              placeholder="Subcategorías"
            />
          </WasteTypesToolbar>
          <Button onClick={openCreate} className="ml-4 shrink-0">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo tipo
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando...</p>
        ) : !wasteTypes?.length ? (
          <p className="text-sm text-muted-foreground">No hay tipos de residuo registrados.</p>
        ) : !filtered.length ? (
          <p className="text-sm text-muted-foreground">No se encontraron tipos para esa búsqueda.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                <th className="pb-2">Categoría</th>
                <th className="pb-2">Subcategoría</th>
                <th className="pb-2">Tipo de material</th>
                <th className="pb-2">Unidad</th>
                <th className="pb-2">Factor CO2e</th>
                <th className="pb-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((wt) => (
                <tr key={wt.id} className="border-b last:border-0">
                  <td className="py-3 text-muted-foreground">
                    {wt.subcategory?.category?.name ?? '—'}
                  </td>
                  <td className="py-3 text-muted-foreground">{wt.subcategory?.name ?? '—'}</td>
                  <td className="py-3 font-medium text-foreground">{wt.name}</td>
                  <td className="py-3 text-muted-foreground">{wt.unit}</td>
                  <td className="py-3 text-muted-foreground">
                    {wt.emission_factor !== null ? wt.emission_factor : 'Sin factor'}
                  </td>
                  <td className="py-3 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mr-1 h-8 w-8"
                      onClick={() => openEdit(wt)}
                      aria-label={`Editar ${wt.name}`}
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openDelete(wt)}
                      aria-label={`Eliminar ${wt.name}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={isFormDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.mode === 'create' ? 'Nuevo tipo de material' : 'Editar tipo de material'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="waste-type-subcategory">Subcategoría</Label>
              <Select value={subcategoryId} onValueChange={setSubcategoryId}>
                <SelectTrigger id="waste-type-subcategory">
                  <SelectValue placeholder="Selecciona una subcategoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => {
                    const subs = subcategories.filter((s) => s.waste_category_id === cat.id)
                    if (!subs.length) return null
                    return (
                      <SelectGroup key={cat.id}>
                        <SelectLabel>{cat.name}</SelectLabel>
                        {subs.map((sub) => (
                          <SelectItem key={sub.id} value={String(sub.id)}>
                            {sub.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="waste-type-name">Nombre</Label>
              <Input
                id="waste-type-name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="waste-type-unit">Unidad</Label>
              <Input
                id="waste-type-unit"
                value={unitInput}
                onChange={(e) => setUnitInput(e.target.value)}
                placeholder="kg"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="waste-type-emission-factor">Factor CO2e</Label>
              <Input
                id="waste-type-emission-factor"
                type="number"
                min="0"
                step="0.0001"
                value={emissionFactorInput}
                onChange={(e) => setEmissionFactorInput(e.target.value)}
                placeholder="kg CO2e evitado por kg material"
              />
              <p className="text-xs text-muted-foreground">kg CO2e evitado por kg material</p>
            </div>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!nameInput.trim() || !subcategoryId || activeMutation.isPending}
            >
              {activeMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialog.mode === 'delete'} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar tipo de material</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/70">
            ¿Estás seguro que deseas eliminar{' '}
            <span className="font-semibold">
              {dialog.mode === 'delete' ? dialog.wasteType.name : ''}
            </span>
            ? Esta acción no se puede deshacer.
          </p>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
